import { Pressable, StyleSheet, useWindowDimensions, View } from 'react-native';
import { HTMLRenderer } from 'shared/components/HTMLRenderer/HTMLRenderer';
import Button, { ButtonProps } from 'shared/components/vxl/Button/Button';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import { isIOS } from 'shared/constants/platform';
import theme from 'shared/styles/theme';

export interface TransactionListItemBannerProps {
  isDismissible?: boolean;
  finePrintText?: string;
  buttons?: ButtonProps[];
  onClose?: () => void;
  body?: string;
}

const TransactionListItemBanner = ({
  body,
  isDismissible = false,
  onClose,
  finePrintText,
  buttons,
}: TransactionListItemBannerProps) => {
  const { width } = useWindowDimensions();

  return (
    <View style={styles.container}>
      <View style={styles.arrow} />
      <View style={styles.body}>
        <View
          style={styles.contentShrink}
          testID="transaction-list-item-banner-body"
        >
          <BodySmText numberOfLines={0} style={styles.text}>
            <HTMLRenderer
              htmlAsString={body ?? ''}
              contentWidth={width}
              tagsStyles={htmlStyles}
            />
          </BodySmText>
        </View>
        {isDismissible ? (
          <Pressable onPress={onClose} role="button">
            <Icon iconKey="CLOSE" iconSize="MD" />
          </Pressable>
        ) : null}
      </View>
      {buttons ? (
        <View style={styles.buttonWrapper}>
          {buttons?.map((item: ButtonProps) => (
            <Button
              key={`Button-${item.label}`}
              label={item.label}
              width="HUG"
              type={item.type}
              onPress={item.onPress}
            />
          ))}
        </View>
      ) : null}
      {finePrintText ? (
        <BodyXSText
          style={styles.finePrintText}
          testID="transaction-list-item-banner-subtext"
          numberOfLines={0}
        >
          {finePrintText}
        </BodyXSText>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  arrow: {
    backgroundColor: theme.color.background.surface,
    height: 15,
    left: 11,
    position: 'absolute',
    top: -7,
    transform: 'rotate(45deg)',
    width: 15,
  },
  body: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  buttonWrapper: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.s,
  },
  container: {
    backgroundColor: theme.color.background.surface,
    borderRadius: theme.spacing.xs,
    display: 'flex',
    gap: theme.spacing.s,
    padding: theme.spacing.m,
    position: 'relative',
    width: '100%',
  },
  contentShrink: {
    flexShrink: 1,
  },
  finePrintText: {
    color: theme.color.text.secondary,
  },
  text: {
    display: 'flex',
    paddingTop: isIOS ? theme.spacing.xs : 0,
  },
});

const htmlStyles = StyleSheet.create({
  // Disabled ESLint for unused styles in this file, as they're passed to the HTMLRenderer component.
  // eslint-disable-next-line react-native/no-unused-styles
  b: {
    fontFamily: theme.typography.body.smBold.fontFamily,
    fontSize: theme.typography.body.smBold.fontSize,
    letterSpacing: theme.typography.body.smBold.letterSpacing,
    lineHeight: theme.typography.body.smBold.lineHeight,
  },
  // eslint-disable-next-line react-native/no-unused-styles
  p: { margin: 0 },
  // eslint-disable-next-line react-native/no-unused-styles
  ul: {
    margin: 0,
  },
});

export default TransactionListItemBanner;
